import React from 'react';
import { Outlet, Navigate } from 'react-router-dom';

import ROUTES from '@constants/routes';

const AthletesList = React.lazy(() => import('@pages/AthletesList'));
const AthleteDetailed = React.lazy(() => import('@pages/AthleteDetailed'));
const Dashboard = React.lazy(() => import('@pages/Dashboard'));
const PagesList = React.lazy(() => import('@pages/PagesList'));
const EventsList = React.lazy(() => import('@pages/EventsList'));
const LocationsList = React.lazy(() => import('@pages/LocationsList'));
const LocationDetailed = React.lazy(() => import('@pages/LocationDetailed'));
const LocationCreate = React.lazy(() => import('@pages/LocationCreate'));
const CampaignsList = React.lazy(() => import('@pages/CampaignsList'));
const CampaignCreate = React.lazy(() => import('@pages/CampaignCreate'));
const CampaignDetailed = React.lazy(() => import('@pages/CampaignDetailed'));
const CompetitionsList = React.lazy(() => import('@pages/CompetitionsList'));
const CompetitionDetailed = React.lazy(() => import('@pages/CompetitionDetailed'));
const MinisitesList = React.lazy(() => import('@pages/MinisitesList'));
const MinisiteDetailed = React.lazy(() => import('@pages/MinisiteDetailed'));
const MinisiteCreate = React.lazy(() => import('@pages/MinisiteCreate'));
const EventDetailed = React.lazy(() => import('@pages/EventDetailed'));
const FlashInterviews = React.lazy(() => import('@pages/FlashInterviews'));
const PageCreate = React.lazy(() => import('@pages/PageCreate'));
const PageDetailed = React.lazy(() => import('@pages/PageDetailed'));
const ArticlesList = React.lazy(() => import('@pages/ArticlesList'));
const ArticleCreate = React.lazy(() => import('@pages/ArticleCreate'));
const ArticleDetailed = React.lazy(() => import('@pages/ArticleDetailed'));
const EmailsList = React.lazy(() => import('@pages/EmailsList'));
const BooksList = React.lazy(() => import('@pages/BooksList'));
const BookCreate = React.lazy(() => import('@pages/BookCreate'));
const BookDetailed = React.lazy(() => import('@pages/BookDetailed'));
const ChapterCreate = React.lazy(() => import('@pages/ChapterCreate'));
const ChaptersList = React.lazy(() => import('@pages/ChaptersList'));
const ChapterDetailed = React.lazy(() => import('@pages/ChapterDetailed'));
const SectionCreate = React.lazy(() => import('@pages/SectionCreate'));
const SectionsList = React.lazy(() => import('@pages/SectionsList'));
const SectionDetailed = React.lazy(() => import('@pages/SectionDetailed'));
const Appearance = React.lazy(() => import('@pages/Appearance'));
const EventAppearance = React.lazy(() => import('@pages/EventAppearance'));
const SponsorsList = React.lazy(() => import('@pages/SponsorsList'));
const SponsorDetailed = React.lazy(() => import('@pages/SponsorDetailed'));
const SponsorCreate = React.lazy(() => import('@pages/SponsorCreate'));

const LanguagesList = React.lazy(() => import('@pages/LanguagesList'));
const LanguageCreate = React.lazy(() => import('@pages/LanguageCreate'));
const LanguageDetailed = React.lazy(() => import('@pages/LanguageDetailed'));
const Vocabulary = React.lazy(() => import('@pages/Vocabulary'));

const EmailCreate = React.lazy(() => import('@pages/EmailCreate'));
const EmailDetailed = React.lazy(() => import('@pages/EmailDetailed'));
const MyAccount = React.lazy(() => import('@pages/MyAccount'));
const Menus = React.lazy(() => import('@pages/Menus'));
const Login = React.lazy(() => import('@pages/Login'));
const Logout = React.lazy(() => import('@pages/Logout'));
const MediaLibrary = React.lazy(() => import('@pages/MediaLibrary'));
const Settings = React.lazy(() => import('@pages/Settings'));
const WorldAthleticsPlus = React.lazy(() => import('@pages/WorldAthleticsPlus'));
const SettingsCis = React.lazy(() => import('@pages/SettingsCis'));
const SettingsStatsZone = React.lazy(() => import('@pages/SettingsStatsZone'));
const SettingsMedalCeremony = React.lazy(() => import('@pages/SettingsMedalCeremony'));
const SettingStatsZoneDetailed = React.lazy(() => import('@pages/StatsZoneUserDetails'));
const SettingMedalCeremonyDetailed = React.lazy(() => import('@pages/MedalCeremonyUserDetails'));
const Forbidden = React.lazy(() => import('@pages/Forbidden'));
const NotFound = React.lazy(() => import('@pages/NotFound'));
const AssetsList = React.lazy(() => import('@pages/AssetsList'));
const AssetCreate = React.lazy(() => import('@pages/AssetCreate'));
const AssetDetailed = React.lazy(() => import('@pages/AssetDetailed'));
const GettyImagesList = React.lazy(() => import('@pages/GettyImagesSearch'));

const WhereToWatchLanding = React.lazy(() => import('@pages/WhereToWatchLanding'));
const WhereToWatchDetailed = React.lazy(() => import('@pages/WhereToWatchDetailed'));
const BroadcastersList = React.lazy(() => import('@pages/BroadcastersList'));
const BroadcasterCreate = React.lazy(() => import('@pages/BroadcasterCreate'));
const BroadcasterDetailed = React.lazy(() => import('@pages/BroadcasterDetailed'));
const EventSchedule = React.lazy(() => import('@pages/EventSchedule'));
const EventScheduleDetailed = React.lazy(() => import('@pages/EventScheduleDetailed'));
const EventScheduleCreate = React.lazy(() => import('@pages/EventScheduleCreate'));
const VideosList = React.lazy(() => import('@pages/VideosList'));
const VideoCreate = React.lazy(() => import('@pages/VideoCreate'));
const VideoDetailed = React.lazy(() => import('@pages/VideoDetailed'));
const PredictorGame = React.lazy(() => import('@pages/PredictorGame'));
const CompetitionMode = React.lazy(() => import('@pages/CompetitionMode'));
const ConfigurationCIS = React.lazy(() => import("@pages/ConfigurationCIS"));
const ConfigureCIS = React.lazy(() => import("@pages/ConfigureCIS"));
const ConfigureCompetition = React.lazy(() => import('@pages/ConfigureCompetition'));
const WinModule = React.lazy(() => import('@pages/WinModule'));
const WinModuleDetailed = React.lazy(() => import('@pages/WinModuleDetailed'));
const WinModuleCreate = React.lazy(() => import('@pages/WindModuleCreate'));
const Indexer = React.lazy(() => import('@pages/Indexer'));

export const unauthenticatedRoutes = [
  {
    path: ROUTES.LOGIN,
    element: <Login />,
  },
  {
    path: ROUTES.LOGOUT,
    element: <Logout />,
  },
];

export const commonRoutes = [
  ...unauthenticatedRoutes,
  {
    path: ROUTES.ROOT,
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    path: ROUTES.DASHBOARD,
    element: <Dashboard />,
  },
  {
    path: ROUTES.MY_ACCOUNT,
    element: <MyAccount />,
  },
  {
    path: ROUTES.PAGES,
    element: <Outlet />,
    children: [
      { path: '/', element: <PagesList /> },
      { path: 'new', element: <PageCreate /> },
      { path: ':id', element: <PageDetailed /> },
      { path: ':id/:locale', element: <PageDetailed /> },
    ],
  },
  {
    path: ROUTES.WHERE_TO_WATCH,
    element: <Outlet />,
    children: [
      { path: '/', element: <WhereToWatchLanding /> },
      { path: ':id', element: <WhereToWatchDetailed /> },
    ],
  },
  {
    path: ROUTES.SCHEDULES,
    element: <Outlet />,
    children: [
      { path: ':id', element: <EventSchedule /> },
      { path: 'edit/:scheduleId', element: <EventScheduleDetailed /> },
      { path: ':eventIdWa/new', element: <EventScheduleCreate /> },
    ],
  },
  {
    path: ROUTES.CHANNELS,
    element: <Outlet />,
    children: [
      { path: '/', element: <BroadcastersList /> },
      { path: 'new', element: <BroadcasterCreate /> },
      { path: ':id', element: <BroadcasterDetailed /> },
    ],
  },
  {
    path: ROUTES.ARTICLES,
    element: <Outlet />,
    children: [
      { path: '/', element: <ArticlesList /> },
      { path: 'new', element: <ArticleCreate /> },
      { path: ':id', element: <ArticleDetailed /> },
      { path: ':id/:locale', element: <ArticleDetailed /> },
    ],
  },
  {
    path: ROUTES.EMAILS,
    element: <Outlet />,
    children: [
      { path: '/', element: <EmailsList /> },
      { path: 'new', element: <EmailCreate /> },
      { path: ':id', element: <EmailDetailed /> },
    ],
  },
  {
    path: ROUTES.SPONSORS,
    element: <Outlet />,
    children: [
      { path: '/', element: <SponsorsList /> },
      { path: 'new', element: <SponsorCreate /> },
      { path: ':id', element: <SponsorDetailed /> },
    ],
  },
  {
    path: ROUTES.LANGUAGES,
    element: <Outlet />,
    children: [
      { path: '/', element: <LanguagesList /> },
      { path: ':id', element: <LanguageDetailed /> },
    ],
  },
  {
    path: ROUTES.VOCABULARY,
    element: <Outlet />,
    children: [{ path: ':locale', element: <Vocabulary /> }],
  },
  {
    path: ROUTES.MEDIA_LIBRARY,
    element: <Outlet />,
    children: [
      { path: '/getty-images', element: <GettyImagesList /> },
      { path: '/', element: <MediaLibrary /> },
    ],
  },
  {
    path: ROUTES.MENUS,
    element: <Menus />,
  },
  {
    path: ROUTES.FORBIDDEN,
    element: <Forbidden />,
  },
];

export const superAdminRoutes = [
  ...commonRoutes,
  {
    path: ROUTES.BOOKS,
    element: <Outlet />,
    children: [
      { path: '/', element: <BooksList /> },
      { path: 'new', element: <BookCreate /> },
      { path: ':id', element: <BookDetailed /> },
      { path: ':id/:locale', element: <BookDetailed /> },
    ],
  },
  {
    path: ROUTES.CHAPTERS,
    element: <Outlet />,
    children: [
      { path: '/', element: <ChaptersList /> },
      { path: 'new', element: <ChapterCreate /> },
      { path: ':id', element: <ChapterDetailed /> },
      { path: ':id/:locale', element: <ChapterDetailed /> },
    ],
  },
  {
    path: ROUTES.SECTIONS,
    element: <Outlet />,
    children: [
      { path: '/', element: <SectionsList /> },
      { path: 'new', element: <SectionCreate /> },
      { path: ':id', element: <SectionDetailed /> },
      { path: ':id/:locale', element: <SectionDetailed /> },
    ],
  },
  {
    path: ROUTES.APPEARANCE,
    element: <Appearance />,
  },
  {
    path: ROUTES.COMPETITIONS,
    element: <Outlet />,
    children: [
      { path: '/', element: <CompetitionsList /> },
      { path: ':id/appearance', element: <CompetitionDetailed /> },
    ],
  },
  {
    path: ROUTES.MINISITES,
    element: <Outlet />,
    children: [
      { path: '/', element: <MinisitesList /> },
      { path: 'new', element: <MinisiteCreate /> },
      { path: ':id', element: <MinisiteDetailed /> },
    ],
  },
  {
    path: ROUTES.LIBRARY,
    element: <Outlet />,
    children: [
      { path: '/', element: <AssetsList /> },
      { path: 'new', element: <AssetCreate /> },
      { path: ':id', element: <AssetDetailed /> },
      { path: ':id/:language', element: <AssetDetailed /> },
    ],
  },
  {
    path: ROUTES.VIDEO,
    element: <Outlet />,
    children: [
      { path: '/', element: <VideosList /> },
      { path: 'new', element: <VideoCreate /> },
      { path: ':id', element: <VideoDetailed /> },
      { path: ':id/:language', element: <VideoDetailed /> },
    ],
  },
  {
    path: ROUTES.LOCATIONS,
    element: <Outlet />,
    children: [
      { path: '/', element: <LocationsList /> },
      { path: 'new', element: <LocationCreate /> },
      { path: ':id', element: <LocationDetailed /> },
    ],
  },
  {
    path: ROUTES.EVENTS,
    element: <Outlet />,
    children: [
      { path: '/', element: <EventsList /> },
      { path: ':eventId/flash-interviews', element: <FlashInterviews /> },
      { path: ':eventIdWa/predictor-game', element: <PredictorGame /> },
      { path: ':id/appearance', element: <EventDetailed /> },
    ],
  },
  {
    path: ROUTES.CAMPAIGNS,
    element: <Outlet />,
    children: [
      { path: '/', element: <CampaignsList /> },
      { path: 'new', element: <CampaignCreate /> },
      { path: ':id', element: <CampaignDetailed /> },
    ],
  },
  {
    path: ROUTES.ATHLETES,
    element: <Outlet />,
    children: [
      { path: '/', element: <AthletesList /> },
      { path: ':id', element: <AthleteDetailed /> },
    ],
  },
  {
    path : ROUTES.COMPETITION_MODE,
    element : <Outlet />,
    children : [
      {
        path : '/',
        element : <CompetitionMode />
      },
      {
        element : <ConfigureCompetition />,
        path : '/configure/:eventId'
      }
    ]
  },
  {
    path: ROUTES.CONFIG_CIS,
    element : <Outlet />,
    children : [
      {
        path : '/', 
        element : <ConfigurationCIS />
      },
      {
        element : <ConfigureCIS />,
        path : '/configure/:eventId'
      }
    ]
  },
  {
    path: ROUTES.WIN_MODULE,
    element: <Outlet />,
    children: [
      { path: '/', element: <WinModule /> },
      { path: '/:id', element: <WinModuleDetailed /> },
      { path: '/new', element: <WinModuleCreate /> },
    ],
  },
  {
    path: ROUTES.WORLD_ATHLETICS_PLUS,
    element: <Outlet />,
    children: [
      {
        path: '/',
        element: <WorldAthleticsPlus />,
      },
    ],
  },
  {
    path: ROUTES.LANGUAGES,
    element: <Outlet />,
    children: [{ path: 'new', element: <LanguageCreate /> }],
  },
  {
    path: ROUTES.SETTINGS,
    element: <Settings />,
  },
  {
    path: ROUTES.SETTINGS_CIS,
    element: <SettingsCis />,
  },
  {
    path: ROUTES.SETTINGS_STATS_ZONE,
    element: <SettingsStatsZone />,
  },
  {
    path: `${ROUTES.SETTINGS_STATS_ZONE}/:id`,
    element: <SettingStatsZoneDetailed />,
  },
  {
    path: ROUTES.SETTINGS_MEDAL_CEREMONY,
    element: <SettingsMedalCeremony />,
  },
  {
    path: `${ROUTES.SETTINGS_MEDAL_CEREMONY}/:id`,
    element: <SettingMedalCeremonyDetailed />,
  },
  {
    path: ROUTES.INDEXER,
    element: <Indexer />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export const LOCadminRoutes = [
  ...commonRoutes,
  {
    path: ROUTES.EVENT_APPEARANCE,
    element: <EventAppearance />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];
